import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const HeroSlideshow = ({ slides, autoPlayInterval = 5000 }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying || slides.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [currentSlide, isAutoPlaying, slides.length, autoPlayInterval]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setIsAutoPlaying(false);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setIsAutoPlaying(false);
  };

  if (!slides || slides.length === 0) return null;

  return (
    <SlideshowContainer>
      {slides.map((slide, index) => (
        <Slide
          key={index}
          $active={index === currentSlide}
          $image={slide.image}
        >
          <SlideOverlay $overlayType={slide.overlayType || 'dark'} />
          <ParallaxContent $active={index === currentSlide}>
            <SlideContent>
              {slide.badge && <Badge>{slide.badge}</Badge>}
              <SlideTitle>{slide.title}</SlideTitle>
              <SlideSubtitle>{slide.subtitle}</SlideSubtitle>
              {slide.description && (
                <SlideDescription>{slide.description}</SlideDescription>
              )}
              {slide.buttons && (
                <SlideButtons>
                  {slide.buttons.map((button, btnIndex) => (
                    <SlideButton
                      key={btnIndex}
                      href={button.link}
                      $variant={button.variant || 'primary'}
                    >
                      {button.text}
                    </SlideButton>
                  ))}
                </SlideButtons>
              )}
            </SlideContent>
          </ParallaxContent>
        </Slide>
      ))}

      {slides.length > 1 && (
        <>
          <NavButton $position="left" onClick={goToPrevious}>
            <FaChevronLeft />
          </NavButton>
          <NavButton $position="right" onClick={goToNext}>
            <FaChevronRight />
          </NavButton>

          <DotsContainer>
            {slides.map((_, index) => (
              <Dot
                key={index}
                $active={index === currentSlide}
                onClick={() => goToSlide(index)}
              />
            ))}
          </DotsContainer>
        </>
      )}
    </SlideshowContainer>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SlideshowContainer = styled.div`
  position: relative;
  height: 100vh;
  min-height: 600px;
  max-height: 900px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.primary};
`;

const Slide = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${({ $image }) => $image});
  background-size: cover;
  background-position: center;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 1s ease-in-out;
  z-index: ${({ $active }) => ($active ? 1 : 0)};
`;

const SlideOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ $overlayType, theme }) => {
    switch ($overlayType) {
      case 'gold':
        return 'linear-gradient(135deg, rgba(10, 25, 41, 0.85) 0%, rgba(212, 175, 55, 0.3) 100%)';
      case 'silver':
        return 'linear-gradient(135deg, rgba(10, 25, 41, 0.85) 0%, rgba(192, 192, 192, 0.3) 100%)';
      case 'blue':
        return 'linear-gradient(135deg, rgba(10, 25, 41, 0.9) 0%, rgba(30, 58, 95, 0.7) 100%)';
      default:
        return 'linear-gradient(135deg, rgba(10, 25, 41, 0.8) 0%, rgba(10, 14, 23, 0.6) 100%)';
    }
  }};
  z-index: 1;
`;

const ParallaxContent = styled.div`
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${({ $active }) => ($active ? slideUp : 'none')} 0.8s ease-out;
`;

const SlideContent = styled.div`
  max-width: 900px;
  padding: 0 2rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Badge = styled.div`
  display: inline-block;
  padding: 0.5rem 1.5rem;
  background: ${({ theme }) => theme.gradients.gold};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  font-size: 0.9rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  border-radius: 50px;
  margin-bottom: 1.5rem;
  box-shadow: ${({ theme }) => theme.shadows.gold};
`;

const SlideTitle = styled.h1`
  font-size: 4.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  text-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
  background: ${({ theme }) => theme.gradients.gold};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 3rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 2.5rem;
  }
`;

const SlideSubtitle = styled.h2`
  font-size: 2rem;
  font-weight: 400;
  margin-bottom: 1.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  color: ${({ theme }) => theme.colors.text.primary};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.75rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 1.5rem;
  }
`;

const SlideDescription = styled.p`
  font-size: 1.25rem;
  max-width: 700px;
  margin: 0 auto 2.5rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.text.secondary};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.1rem;
  }
`;

const SlideButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const SlideButton = styled.a`
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 8px;
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.medium};
  cursor: pointer;
  
  ${({ $variant, theme }) => {
    if ($variant === 'gold') {
      return `
        background: ${theme.gradients.gold};
        color: ${theme.colors.primary};
        box-shadow: ${theme.shadows.gold};
        
        &:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 20px rgba(212, 175, 55, 0.5);
        }
      `;
    } else if ($variant === 'outline') {
      return `
        background: transparent;
        color: ${theme.colors.text.primary};
        border: 2px solid ${theme.colors.metallic.gold};
        
        &:hover {
          background: ${theme.colors.metallic.gold};
          color: ${theme.colors.primary};
          transform: translateY(-3px);
        }
      `;
    } else {
      return `
        background: ${theme.colors.secondary};
        color: ${theme.colors.text.primary};
        box-shadow: ${theme.shadows.md};
        
        &:hover {
          background: ${theme.colors.tertiary};
          transform: translateY(-3px);
          box-shadow: ${theme.shadows.lg};
        }
      `;
    }
  }}
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  ${({ $position }) => ($position === 'left' ? 'left: 2rem' : 'right: 2rem')};
  transform: translateY(-50%);
  z-index: 10;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(212, 175, 55, 0.5);
  color: ${({ theme }) => theme.colors.metallic.gold};
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.medium};
  
  &:hover {
    background: ${({ theme }) => theme.colors.metallic.gold};
    color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-50%) scale(1.1);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
    ${({ $position }) => ($position === 'left' ? 'left: 1rem' : 'right: 1rem')};
  }
`;

const DotsContainer = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  gap: 0.75rem;
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.metallic.gold};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.metallic.gold : 'transparent'};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.short};
  
  &:hover {
    transform: scale(1.3);
    background: ${({ theme }) => theme.colors.metallic.goldLight};
  }
`;

export default HeroSlideshow;
